import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useErrorBoundary } from 'react-error-boundary';
import { apiClient } from './services/apiClient';
import { frontendEventsService } from './services/frontendEventsService';
import { paymentHandler } from './services/paymentHandler';
import { initI18n } from './i18n';
import { CheckoutContext } from './context/CheckoutContext';
import { useSubscriptionSummary, useFinalizeSubscription, useCloseSubscription } from './api/subscription';
import { useChargebee } from './hooks/useChargebee';
import { useChargebeePaymentIntent } from './hooks/useChargebeePaymentIntent';
import { useSubscriptionPolling } from './hooks/useSubscriptionPolling';
import { usePayment } from './hooks/usePayment';
import { useCardElementRef } from './hooks/useStripeElements';
import { useTranslate } from './hooks/useTranslate';
import { getBoot, hasBootParam } from './utils/boot';
import {
  buildSubscriptionCheckoutContext,
  buildSubscriptionCheckoutData,
  buildSubscriptionPaymentMethods,
  createSubscriptionFallbackBoot,
} from './utils/subscriptionContext';
import { isDuplicateSubscriptionError } from './utils/subscriptionErrors';
import { closeScreenSurface } from './utils/screenSurface';
import { TemplateType } from './types/boot';
import { isChargebeeUserClosedError } from './utils/chargebeeWallets';
import { getAppchargeErrorMessage, getRawApiErrorMessage } from './utils/error';
import {
  notifyCheckoutOpened,
  notifyCloseCheckout,
  notifyOrderCreated,
  notifyOrderFailed,
  notifyOrderSuccess,
  notifyPaymentIntentFailed,
  notifyPaymentIntentSuccess,
} from './utils/notifier';
import { getEventDataForCloseCheckout, getEventDataForSdkEvents } from './utils/eventDataBuilder';
import { identifyCheckoutUser } from './utils/checkoutMixpanelTracking';
import { shouldCloseCheckoutOnHiddenSuccessScreen } from './utils/conditionalRendering';
import { initMixpanel } from './utils/mixpanelInit';
import { FeatureFlag } from './constants/featureFlags';
import { resolveIsRedirectFlow } from './utils/checkoutMode';
import { resolveAttributedPaymentMethod } from './utils/paymentMethodResolvers';
import { logError, logInfo } from './utils/logger';
import { ErrorLogs, InfoLogs } from './constants/logs';
import { PaymentMethodEnum, ProviderEnum, type ExcludeOtherPaymentMethod } from './types/common';
import { PollingStatus } from './types/order';
import { FinalizeSubscriptionStatus, type FinalizeSubscriptionResponse } from './types/subscription';
import type { ChargebeePaymentIntent } from './types/chargebee';
import type { BootData } from './types/boot';
import type { Customer, PriceBreakdown, Pricing } from './types/checkout';
import type { AddressDetails, ICheckoutContext, SuccessScreenContext } from './types/context';
import type { PayFromExpressCheckout } from './types/payment';
import type { DrawerScreen } from './types/screens';
import type { CardDetails } from './types/adyen';
import type { SubmitActions } from '@adyen/adyen-web';
import CheckoutSkeletonLoader from './components/CheckoutSkeletonLoader/CheckoutSkeletonLoader';
import { SubscriptionView } from './templateViews/SubscriptionView';

/**
 * Controller for the `/subscriptions/:token` (Chargebee) route. Deliberately
 * thin next to CheckoutContent: fetch the summary, adapt it into the standard
 * `ICheckoutContext` via {@link buildSubscriptionCheckoutContext}, dispatch
 * payments through the existing `paymentHandler` registry with
 * `sendSubscriptionToServer` injected as the finalize step, then poll the
 * subscription until it turns ACTIVE.
 */
export const SubscriptionCheckoutContent = () => {
  const { token } = useParams<{ token: string }>();
  const { showBoundary } = useErrorBoundary();
  const { t } = useTranslate();

  // Boot is optional here: v2-generated subscription links may carry no
  // `#boot=` hash (getBoot() never settles without one). Fall back to a
  // minimal boot — only theming/flags degrade, never the flow.
  const [boot, setBoot] = useState<BootData | null>(null);
  const [bootReady, setBootReady] = useState(() => !hasBootParam());
  useEffect(() => {
    if (!hasBootParam()) return;
    getBoot()
      .then((bootData) => {
        setBoot(bootData);
        setBootReady(true);
      })
      .catch(() => showBoundary('unableToLoadCheckout'));
  }, [showBoundary]);

  const [isApiInitilized, setIsApiInitilized] = useState(false);
  useEffect(() => {
    if (!token || isApiInitilized) return;
    apiClient.initilize({ token });
    frontendEventsService.initialize({ sessionToken: token });
    initMixpanel(FeatureFlag.ENABLE_SUBSCRIPTIONS_SESSION_RECORDING, 'subscriptions');
    initI18n(token).catch((error) => logError('Failed to initialize i18n', error));
    setIsApiInitilized(true);
  }, [token, isApiInitilized]);

  const summaryQuery = useSubscriptionSummary({ enableFetch: isApiInitilized && bootReady });
  const summary = summaryQuery.data;

  // Mutable pieces of the summary (email edit, ZIP tax recalc, refreshed intent).
  const [customer, setCustomer] = useState<Customer>();
  const [pricing, setPricing] = useState<Pricing>();
  const [paymentIntent, setPaymentIntent] = useState<ChargebeePaymentIntent>();
  const [addressDetails, setAddressDetails] = useState<AddressDetails>();
  const [paymentMethodSelected, setPaymentMethodSelected] = useState<PaymentMethodEnum>(PaymentMethodEnum.CREDIT_CARD);
  // Summary APMs past the fourth box live under the "Other" tile; this is the
  // one the shopper picked there (same contract as useCheckout's).
  const [otherMethodSelected, setOtherMethodSelected] = useState<ExcludeOtherPaymentMethod>();
  const payment = usePayment();
  const [error, setError] = useState<string | null>(null);
  const [successScreen, setSuccessScreen] = useState<SuccessScreenContext>({ isOpen: false });
  const [viewerScreen, setViewerScreen] = useState<DrawerScreen>();
  const [drawerScreen, setDrawerScreen] = useState<DrawerScreen>();
  const { cardElementRef, setCardElementRef } = useCardElementRef();
  // Required by CardContext but never used on this route (no Adyen).
  const adyenCardDetailsRef = useRef({} as CardDetails & { actions?: SubmitActions });

  const finalizeSubscription = useFinalizeSubscription();
  const closeSubscription = useCloseSubscription();
  const { cbInstance } = useChargebee(summary?.pks?.chargebee);
  const [isPaymentIntentUnavailable, setIsPaymentIntentUnavailable] = useState(false);
  // The summary's intent is `{ id }` only; the card component and both wallet
  // handlers reject anything without amount/status/gateway.
  useChargebeePaymentIntent({
    cbInstance,
    paymentIntentId: summary?.paymentIntent?.id,
    priceTotal: pricing?.priceBreakdown?.priceTotal,
    onHydrated: useCallback((intent: ChargebeePaymentIntent) => {
      setIsPaymentIntentUnavailable(false);
      setPaymentIntent(intent);
    }, []),
    onUnavailable: useCallback(() => setIsPaymentIntentUnavailable(true), []),
  });
  const {
    triggerSubscriptionPolling,
    statusSubscriptionPolling,
    error: subscriptionPollingError,
    errorCode: subscriptionPollingErrorCode,
  } = useSubscriptionPolling();

  const lastPaymentMethodUsedRef = useRef<PaymentMethodEnum>();
  const contextRef = useRef<ICheckoutContext | null>(null);
  const seededRef = useRef(false);
  const openedNotifiedRef = useRef(false);
  const fallbackBoot = useMemo(() => createSubscriptionFallbackBoot(), []);
  const bootData = boot ?? fallbackBoot;

  useEffect(() => {
    if (!summary || seededRef.current) return;
    seededRef.current = true;
    setCustomer(summary.customer);
    setPricing(summary.pricing);
    setPaymentIntent(summary.paymentIntent);
    setAddressDetails({
      address: summary.customer?.address?.address ?? '',
      zipCode: summary.customer?.address?.zipCode ?? summary.customer?.geoLocator?.zipCode ?? '',
    });
  }, [summary]);

  useEffect(() => {
    if (!summaryQuery.error) return;
    logError(ErrorLogs.checkoutPaymentError, { error: summaryQuery.error });
    showBoundary(
      isDuplicateSubscriptionError(summaryQuery.error) ? 'activeSubscriptionExists' : 'unableToLoadCheckout'
    );
  }, [summaryQuery.error, showBoundary]);

  // Shaped like CheckoutData purely for the SDK event builders.
  const eventCheckoutData = useMemo(
    () =>
      summary ? buildSubscriptionCheckoutData(summary, bootData, buildSubscriptionPaymentMethods(summary)) : undefined,
    [summary, bootData]
  );

  useEffect(() => {
    if (!eventCheckoutData || openedNotifiedRef.current) return;
    openedNotifiedRef.current = true;
    if (boot) frontendEventsService.setPublisherId(boot.publisherData.id);
    notifyCheckoutOpened(getEventDataForSdkEvents(eventCheckoutData));
    notifyOrderCreated(getEventDataForSdkEvents(eventCheckoutData));
  }, [eventCheckoutData, boot]);

  // Keyed on the STABLE `mutate` (the mutation result object is a new identity
  // every render, which would rebuild the checkout context on every render).
  const closeSubscriptionMutate = closeSubscription.mutate;
  const closeCheckout = useCallback(
    (context: ICheckoutContext, reason?: string) => {
      // Only release the session while nothing was finalized — mirrors v2,
      // which POSTs /close only from the INIT state.
      if (statusSubscriptionPolling === PollingStatus.IDLE) {
        closeSubscriptionMutate();
      }
      notifyCloseCheckout(getEventDataForCloseCheckout(context, { error: { reason } }));
    },
    [closeSubscriptionMutate, statusSubscriptionPolling]
  );

  const checkoutContext = useMemo<ICheckoutContext | undefined>(() => {
    if (!summary || !customer || !pricing || !paymentIntent || !bootReady) return undefined;

    // The seam that makes the shared payment handlers finalize a SUBSCRIPTION:
    // `paymentHandler.processPayment` already takes the finalize step as an
    // argument, so injecting this swaps `/finalize-payment` for
    // `/checkout/subscriptions/finalize-subscription` with zero registry changes.
    // Generic signature matches `paymentHandler.processPayment`'s contract;
    // the request body is fixed, so whatever a handler passes is ignored.
    const sendSubscriptionToServer = async <TResponse = unknown,>(): Promise<TResponse> => {
      return (await finalizeSubscription.mutateAsync({
        params: { planId: summary.plan._id },
        customer: { id: customer.id, email: customer.email },
      })) as TResponse;
    };

    const runPayment = async <TResponse = unknown,>(
      params?: unknown,
      payFromExpressCheckout?: PayFromExpressCheckout
    ): Promise<TResponse | undefined> => {
      // An overflow APM under the "Other" tile is attributed as itself
      // (kakao_pay, not `other`) in logs and SDK events — as in the one-off checkout.
      const method = resolveAttributedPaymentMethod(paymentMethodSelected, otherMethodSelected, payFromExpressCheckout);
      try {
        setError(null);
        lastPaymentMethodUsedRef.current = method;
        logInfo(
          InfoLogs.checkoutPaymentStarted,
          { provider: ProviderEnum.CHARGEBEE, payFromExpressCheckout },
          {
            payment_method: method,
            payment_provider: ProviderEnum.CHARGEBEE,
            pay_from_express: Boolean(payFromExpressCheckout),
          }
        );
        if (payFromExpressCheckout) {
          payment.setIsExpressPaymentInProcess(true);
        } else {
          payment.setIsInPaymentProcess(true);
        }

        const results = await paymentHandler.processPayment(
          method,
          contextRef.current!,
          sendSubscriptionToServer,
          params
        );
        const finalize = results as FinalizeSubscriptionResponse;
        if (finalize?.status !== FinalizeSubscriptionStatus.SUCCESS || !finalize.subscriptionId) {
          throw new Error('Subscription finalize failed');
        }
        if (eventCheckoutData) {
          notifyPaymentIntentSuccess(getEventDataForSdkEvents(eventCheckoutData, { paymentMethodName: method }));
        }
        // Processing stays on until polling turns the subscription ACTIVE.
        triggerSubscriptionPolling(finalize.subscriptionId);
        return results as TResponse;
      } catch (error: unknown) {
        logError(ErrorLogs.checkoutPaymentError, { error });
        // Wallet-sheet dismissals reject too — reset silently, no decline copy.
        if (!isChargebeeUserClosedError(error)) {
          setError(getAppchargeErrorMessage(t));
          if (eventCheckoutData) {
            notifyPaymentIntentFailed(eventCheckoutData, method, { reason: getRawApiErrorMessage(error) });
          }
        }
        payment.setIsInPaymentProcess(false);
        payment.setIsExpressPaymentInProcess(false);
      }
    };

    const context = buildSubscriptionCheckoutContext({
      summary,
      boot: bootData,
      sessionId: token ?? '',
      customer,
      pricing,
      subscription: {
        plan: summary.plan,
        paymentIntent,
        cbInstance,
        isPaymentIntentUnavailable,
        setPaymentIntent,
      },
      paymentMethodSelected,
      otherMethodSelected,
      addressDetails,
      successScreen,
      error,
      payment: {
        isInPaymentProcess: payment.isInPaymentProcess,
        isExpressPaymentInProcess: payment.isExpressPaymentInProcess,
        isFormValid: payment.isFormValid,
      },
      card: { cardElementRef, setCardElementRef, adyenCardDetailsRef },
      viewer: {
        screen: viewerScreen,
        setViewer: setViewerScreen,
        closeViewer: () => setViewerScreen(undefined),
      },
      drawer: {
        screen: drawerScreen,
        setDrawerScreen,
        // Screens (e.g. the "Other" method list) close through this for both
        // surfaces; on desktop they live on the viewer, so clear that one too.
        closeDrawer: () => closeScreenSurface(TemplateType.DEFAULT, setViewerScreen, setDrawerScreen),
      },
      actions: {
        runPayment,
        sendPaymentToServer: sendSubscriptionToServer,
        setPaymentMethodSelected,
        updateOtherMethodSelected: setOtherMethodSelected,
        setCustomerEmail: (email: string) => setCustomer((prev) => (prev ? { ...prev, email } : prev)),
        setIsFormValid: payment.setIsFormValid,
        setIsInPaymentProcess: payment.setIsInPaymentProcess,
        setIsExpressPaymentInProcess: payment.setIsExpressPaymentInProcess,
        updatePriceBreakdown: (priceBreakdown: PriceBreakdown) =>
          setPricing((prev) => (prev ? { ...prev, priceBreakdown } : prev)),
        updateAddressDetails: setAddressDetails,
        setError,
        setSuccessScreen,
        closeCheckout,
      },
    });
    contextRef.current = context;
    return context;
    // `payment` setters are stable; `t` identity churns with i18n loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    summary,
    customer,
    pricing,
    paymentIntent,
    bootReady,
    bootData,
    token,
    cbInstance,
    isPaymentIntentUnavailable,
    paymentMethodSelected,
    otherMethodSelected,
    addressDetails,
    successScreen,
    error,
    payment.isInPaymentProcess,
    payment.isExpressPaymentInProcess,
    payment.isFormValid,
    cardElementRef,
    setCardElementRef,
    viewerScreen,
    drawerScreen,
    eventCheckoutData,
    closeCheckout,
    triggerSubscriptionPolling,
  ]);

  // Identify the player before wallet loading completes, so early-exit replays
  // are not anonymous. Does nothing if already identified or publisher data is missing.
  useEffect(() => {
    if (checkoutContext) identifyCheckoutUser(checkoutContext);
  }, [checkoutContext]);

  useEffect(() => {
    if (statusSubscriptionPolling === PollingStatus.SUCCESS) {
      payment.setIsInPaymentProcess(false);
      payment.setIsExpressPaymentInProcess(false);
      setSuccessScreen({ isOpen: true });
      if (eventCheckoutData) {
        notifyOrderSuccess(
          getEventDataForSdkEvents(eventCheckoutData, { paymentMethodName: lastPaymentMethodUsedRef.current })
        );
      }
      if (
        checkoutContext &&
        shouldCloseCheckoutOnHiddenSuccessScreen(
          checkoutContext.featureFlags,
          resolveIsRedirectFlow(checkoutContext.checkoutConfiguration)
        )
      ) {
        closeCheckout(checkoutContext);
      }
    }
    if (statusSubscriptionPolling === PollingStatus.FAILED) {
      if (eventCheckoutData) {
        notifyOrderFailed(
          getEventDataForSdkEvents(eventCheckoutData, {
            paymentMethodName: lastPaymentMethodUsedRef.current,
            reason: subscriptionPollingError,
            error: { reason: subscriptionPollingError, code: subscriptionPollingErrorCode, isFatal: true },
          })
        );
      }
      showBoundary('unableToCompleteOrder');
    }
    // Terminal SDK events must fire exactly once per status transition (same
    // rationale as CheckoutContent's order-polling effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSubscriptionPolling]);

  if (!checkoutContext) {
    return (
      <div className="checkout-boot-stage checkout-boot-stage--loading">
        <CheckoutSkeletonLoader />
      </div>
    );
  }

  return (
    <CheckoutContext.Provider value={checkoutContext}>
      <SubscriptionView statusOrderPolling={statusSubscriptionPolling} />
    </CheckoutContext.Provider>
  );
};
